import { supabase } from '../lib/supabase';

// CRITICAL FIX: Get effective user ID for all queries
const getEffectiveUserId = async () => {
  // Check for impersonation first
  const impersonationData = localStorage.getItem('admin_impersonation');
  if (impersonationData) {
    const { impersonatedUserId } = JSON.parse(impersonationData);
    return impersonatedUserId;
  }
  
  // Fall back to authenticated user
  const { data: { user } } = await supabase?.auth?.getUser();
  return user?.id || null;
};

// NEW: Fetch user's currency preference
const getUserCurrency = async (userId) => {
  const { data, error } = await supabase
    ?.from('user_profiles')
    ?.select('currency')
    ?.eq('id', userId)
    ?.single();

  if (error) {
    console.error('Error fetching user currency:', error);
    return 'USD'; // Default fallback
  }

  return data?.currency || 'USD';
};

export const dashboardService = {
  // Get project management summary
  async getProjectSummary() {
    const userId = await getEffectiveUserId();
    if (!userId) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('project_tasks')
      ?.select('status, archived_at, due_date')
      ?.eq('user_id', userId);

    if (error) throw error;

    // Filter out archived tasks
    const activeTasks = data?.filter(t => !t?.archived_at) || [];
    
    // Calculate overdue tasks
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    const overdueTasks = activeTasks?.filter(t => {
      if (!t?.due_date) return false;
      const dueDate = new Date(t?.due_date);
      dueDate?.setHours(0, 0, 0, 0);
      return dueDate < today && t?.status !== 'completed';
    });

    return {
      total: activeTasks?.length,
      toDo: activeTasks?.filter(t => t?.status === 'to_do')?.length,
      inProgress: activeTasks?.filter(t => t?.status === 'in_progress')?.length,
      completed: activeTasks?.filter(t => t?.status === 'completed')?.length,
      overdue: overdueTasks?.length
    };
  },

  // Get budget management summary
  async getBudgetSummary() {
    const userId = await getEffectiveUserId();
    if (!userId) throw new Error('Not authenticated');

    // CRITICAL FIX: Fetch user's currency preference
    const userCurrency = await getUserCurrency(userId);

    // Get budget categories with their types and amounts
    const { data: categories, error: catError } = await supabase
      ?.from('budget_categories')
      ?.select('id, category_type, budget_amount')
      ?.eq('user_id', userId);

    if (catError) throw catError;

    // Build category lookup map
    const categoryMap = {};
    categories?.forEach(cat => {
      categoryMap[cat?.id] = {
        type: cat?.category_type,
        budgetAmount: parseFloat(cat?.budget_amount) || 0
      };
    });

    // Get ALL budget records (not just current month) to calculate totals accurately
    const { data: records, error: recError } = await supabase
      ?.from('budget_records')
      ?.select('amount, category_id, transaction_date')
      ?.eq('user_id', userId)
      ?.order('transaction_date', { ascending: false });

    if (recError) throw recError;

    // Calculate current month's income and expenses
    const now = new Date();
    const firstDayOfMonth = new Date(now?.getFullYear(), now?.getMonth(), 1);
    const lastDayOfMonth = new Date(now?.getFullYear(), now?.getMonth() + 1, 0);

    let totalIncome = 0;
    let totalExpense = 0;
    let monthlyIncome = 0;
    let monthlyExpense = 0;

    records?.forEach(record => {
      const category = categoryMap?.[record?.category_id];
      if (!category) return;

      const amount = parseFloat(record?.amount) || 0;
      const transactionDate = new Date(record?.transaction_date);

      // Add to monthly totals if within current month
      const isCurrentMonth = transactionDate >= firstDayOfMonth && transactionDate <= lastDayOfMonth;

      if (category?.type === 'income') {
        totalIncome += amount;
        if (isCurrentMonth) monthlyIncome += amount;
      } else if (category?.type === 'expense') {
        totalExpense += amount;
        if (isCurrentMonth) monthlyExpense += amount;
      }
    });

    // Calculate net cash flow (using monthly data for current month)
    const netCashFlow = monthlyIncome - monthlyExpense;

    // Calculate budget targets from categories
    const incomeTarget = categories
      ?.filter(c => c?.category_type === 'income')
      ?.reduce((sum, c) => sum + (parseFloat(c?.budget_amount) || 0), 0);

    const expenseTarget = categories
      ?.filter(c => c?.category_type === 'expense')
      ?.reduce((sum, c) => sum + (parseFloat(c?.budget_amount) || 0), 0);

    // Calculate progress percentages (using monthly data)
    const incomeProgress = incomeTarget > 0 ? (monthlyIncome / incomeTarget) * 100 : 0;
    const expenseProgress = expenseTarget > 0 ? (monthlyExpense / expenseTarget) * 100 : 0;

    return {
      totalIncome: monthlyIncome,  // Current month income
      totalExpense: monthlyExpense,  // Current month expense
      netCashFlow,  // Current month net cash flow
      incomeTarget,  // Monthly income target from categories
      expenseTarget,  // Monthly expense target from categories
      incomeProgress,  // Percentage of income target achieved
      expenseProgress,  // Percentage of expense target achieved
      allTimeIncome: totalIncome,  // Total income across all time
      allTimeExpense: totalExpense,  // Total expense across all time
      currency: userCurrency  // CRITICAL FIX: Return user's currency preference
    };
  },

  // Get asset management summary
  async getAssetSummary() {
    const userId = await getEffectiveUserId();
    if (!userId) throw new Error('Not authenticated');

    // CRITICAL FIX: Fetch user's currency preference
    const userCurrency = await getUserCurrency(userId);

    const { data, error } = await supabase
      ?.from('assets')
      ?.select('asset_value, currency, asset_type, warranty_expiry, is_insured')
      ?.eq('user_id', userId);

    if (error) throw error;

    const totalValue = data?.reduce((sum, asset) => sum + (parseFloat(asset?.asset_value) || 0), 0) || 0;

    // Calculate warranty expiring soon (within 90 days)
    const today = new Date();
    const ninetyDaysFromNow = new Date();
    ninetyDaysFromNow?.setDate(today?.getDate() + 90);

    const warrantyExpiringSoon = data?.filter(asset => {
      if (!asset?.warranty_expiry) return false;
      const expiryDate = new Date(asset?.warranty_expiry);
      return expiryDate > today && expiryDate <= ninetyDaysFromNow;
    })?.length || 0;

    // Asset type breakdown
    const assetTypeBreakdown = {};
    data?.forEach(asset => {
      const type = asset?.asset_type || 'other';
      assetTypeBreakdown[type] = (assetTypeBreakdown?.[type] || 0) + 1;
    });

    const insuredAssets = data?.filter(a => a?.is_insured)?.length || 0;
    const uninsuredAssets = (data?.length || 0) - insuredAssets;

    return {
      totalAssets: data?.length || 0,
      totalValue,
      currency: userCurrency,  // CRITICAL FIX: Use user's currency preference instead of asset's currency
      warrantyExpiringSoon,
      insuredAssets,
      uninsuredAssets,
      assetTypeBreakdown
    };
  },

  // Get complete dashboard overview
  async getDashboardOverview() {
    try {
      // CRITICAL FIX: Use getEffectiveUserId instead of direct user.id
      const userId = await getEffectiveUserId();
      
      if (!userId) throw new Error('User not authenticated');

      // CRITICAL FIX: Call individual summary methods to get all required data
      const projectsData = await this.getProjectSummary();
      const budgetData = await this.getBudgetSummary();
      const assetsData = await this.getAssetSummary();

      // CRITICAL FIX: Use userId from getEffectiveUserId for sales query
      // Fetch Sales Performance Data
      const { data: salesRecords, error: salesError } = await supabase
        ?.from('monthly_sales_performance')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('tracking_month', { ascending: false })
        ?.limit(12);

      if (salesError) throw salesError;

      // Calculate sales metrics
      const totalRevenue = salesRecords?.reduce((sum, record) => 
        sum + (record?.existing_client_revenue || 0) + (record?.new_client_revenue || 0), 0
      ) || 0;
      
      const totalSales = salesRecords?.reduce((sum, record) => 
        sum + (record?.existing_client_sales || 0) + (record?.new_client_sales || 0), 0
      ) || 0;

      const averageROAS = salesRecords?.length > 0
        ? salesRecords?.reduce((sum, record) => sum + (record?.roas || 0), 0) / salesRecords?.length
        : 0;

      const totalExpenses = salesRecords?.reduce((sum, record) => 
        sum + (record?.other_monthly_costs || 0) + (record?.google_ads_expense || 0), 0
      ) || 0;

      const averageProfitMargin = totalRevenue > 0
        ? ((totalRevenue - totalExpenses) / totalRevenue) * 100
        : 0;

      // Calculate revenue trend (comparing last 3 months vs previous 3 months)
      let revenueTrend = 0;
      if (salesRecords?.length >= 6) {
        const recentRevenue = salesRecords?.slice(0, 3)?.reduce((sum, record) => 
          sum + (record?.existing_client_revenue || 0) + (record?.new_client_revenue || 0), 0
        );
        const previousRevenue = salesRecords?.slice(3, 6)?.reduce((sum, record) => 
          sum + (record?.existing_client_revenue || 0) + (record?.new_client_revenue || 0), 0
        );
        
        if (previousRevenue > 0) {
          revenueTrend = ((recentRevenue - previousRevenue) / previousRevenue) * 100;
        }
      }

      // Get currency from user profile - use userId for consistency
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('currency')
        ?.eq('id', userId)
        ?.single();

      // CRITICAL FIX: Use data from individual summary method calls
      return {
        projects: projectsData,
        budget: budgetData,
        assets: assetsData,
        sales: {
          totalRevenue,
          totalSales,
          averageROAS,
          averageProfitMargin,
          revenueTrend,
          currency: profile?.currency || salesRecords?.[0]?.currency || 'USD'
        }
      };
    } catch (error) {
      console.error('Error fetching dashboard overview:', error);
      throw error;
    }
  }
};